import {
  Form, Input, Select, InputNumber, Row, Col, Switch, Typography, Divider, Alert
} from 'antd';
import {
  PUBLISH_STATUSES, VISIBILITIES, SLUG_PATTERN_SRC, humaniseEnum
} from '../../studioConstants';

const { TextArea } = Input;
const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Step 5 — Ecommerce / Catalog.
//
// Optional. The whole step lives behind one master toggle the parent
// owns (`enabledSections.ecommerce`) — most internal-ERP items will
// not need this. When enabled, the spec maps 1:1 to the
// ecommerce_product_profiles backend table.
// ─────────────────────────────────────────────────────────────────

function NumberOrText({ disabled, ...rest }) {
  return <InputNumber disabled={disabled} {...rest} />;
}

export default function EcommerceProfileStep({
  disabled, ecommerceEnabled, onToggleEcommerce
}) {
  return (
    <div>
      <h2 className="erp-studio-section-title">Ecommerce profile</h2>
      <p className="erp-studio-section-help">
        Enable this only if this item should appear in catalog, website or customer enquiry
        screens. Most internal ERP items can leave this off.
      </p>

      <div className={`erp-studio-toggle-card ${ecommerceEnabled ? 'is-on' : ''}`}>
        <div className="erp-studio-toggle-header">
          <div>
            <h3>Enable ecommerce / catalog profile</h3>
            <p>Stores SEO, catalog copy, visibility and publish-lifecycle for this item.</p>
          </div>
          <Switch checked={ecommerceEnabled} onChange={onToggleEcommerce} disabled={disabled} />
        </div>
        {ecommerceEnabled ? (
          <div className="erp-studio-toggle-body">
            <Text strong style={{ fontSize: 13 }}>Catalog content</Text>
            <Row gutter={[12, 0]} style={{ marginTop: 8 }}>
              <Col xs={24} md={12}>
                <Form.Item label="Ecommerce title" name="ecommerceTitle" rules={[{ max: 180 }]}>
                  <Input disabled={disabled} placeholder="Public-facing product title." />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Slug"
                  name="ecommerceSlug"
                  normalize={(v) => (v === undefined || v === null || v === '')
                    ? v : String(v).trim().toLowerCase()}
                  rules={[
                    { max: 180, message: 'Max 180 characters.' },
                    {
                      pattern: new RegExp(SLUG_PATTERN_SRC),
                      message: 'Lowercase letters, digits and single hyphens only.'
                    }
                  ]}
                  tooltip="URL-friendly identifier. Required to publish."
                >
                  <Input disabled={disabled} placeholder="e.g. 4mm-toughened-clear-glass" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Short description" name="shortDescription" rules={[{ max: 2000 }]}>
              <TextArea autoSize={{ minRows: 2, maxRows: 3 }} disabled={disabled} />
            </Form.Item>
            <Form.Item label="Long description" name="longDescription" rules={[{ max: 20000 }]}>
              <TextArea autoSize={{ minRows: 3, maxRows: 6 }} disabled={disabled} />
            </Form.Item>

            <Row gutter={[12, 0]}>
              <Col xs={24} md={12}>
                <Form.Item label="Highlights" name="productHighlights"
                  tooltip="Enter one bullet per line. Max 20 lines.">
                  <Select
                    mode="tags"
                    open={false}
                    tokenSeparators={[',']}
                    placeholder="Enter highlights and press comma…"
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Tags" name="productTags"
                  tooltip="Search keywords for this item. Lowercase, single words preferred.">
                  <Select
                    mode="tags"
                    open={false}
                    tokenSeparators={[',']}
                    placeholder="Add tags…"
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Care instructions" name="careInstructions" rules={[{ max: 4000 }]}>
              <TextArea autoSize={{ minRows: 2, maxRows: 4 }} disabled={disabled} />
            </Form.Item>
            <Form.Item label="Warranty text" name="warrantyText" rules={[{ max: 2000 }]}>
              <TextArea autoSize={{ minRows: 2, maxRows: 3 }} disabled={disabled} />
            </Form.Item>

            <Divider className="erp-studio-divider" />
            <Text strong style={{ fontSize: 13 }}>Catalog classification</Text>
            <Row gutter={[12, 0]} style={{ marginTop: 8 }}>
              <Col xs={24} md={8}>
                <Form.Item label="Category" name="ecommerceCategory" rules={[{ max: 120 }]}>
                  <Input disabled={disabled} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Subcategory" name="ecommerceSubcategory" rules={[{ max: 120 }]}>
                  <Input disabled={disabled} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Collection" name="productCollection" rules={[{ max: 120 }]}>
                  <Input disabled={disabled} />
                </Form.Item>
              </Col>
            </Row>

            <Divider className="erp-studio-divider" />
            <Text strong style={{ fontSize: 13 }}>Visibility & publishing</Text>
            <Alert
              type="info" showIcon
              style={{ margin: '8px 0', fontSize: 12 }}
              message='To publish, fill the slug, set visibility to "public" or "unlisted", and turn on Visible online.'
            />
            <Row gutter={[12, 0]}>
              <Col xs={24} md={8}>
                <Form.Item label="Publish status" name="publishStatus">
                  <Select disabled={disabled}
                    options={PUBLISH_STATUSES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Visibility" name="visibility">
                  <Select disabled={disabled}
                    options={VISIBILITIES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Display order" name="displayOrder">
                  <NumberOrText min={0} step={1} disabled={disabled} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <div className="erp-studio-flags">
              <Form.Item label="Visible online" name="isVisibleOnline" valuePropName="checked" labelCol={{ span: 14 }}>
                <Switch disabled={disabled} />
              </Form.Item>
              <Form.Item label="Featured" name="isFeatured" valuePropName="checked" labelCol={{ span: 14 }}>
                <Switch disabled={disabled} />
              </Form.Item>
              <Form.Item label="New arrival" name="isNewArrival" valuePropName="checked" labelCol={{ span: 14 }}>
                <Switch disabled={disabled} />
              </Form.Item>
              <Form.Item label="Best seller" name="isBestSeller" valuePropName="checked" labelCol={{ span: 14 }}>
                <Switch disabled={disabled} />
              </Form.Item>
              <Form.Item label="Customizable" name="isCustomizable" valuePropName="checked" labelCol={{ span: 14 }}>
                <Switch disabled={disabled} />
              </Form.Item>
              <Form.Item label="Allow enquiry" name="allowOnlineEnquiry" valuePropName="checked" labelCol={{ span: 14 }}>
                <Switch disabled={disabled} />
              </Form.Item>
              <Form.Item label="Allow order" name="allowOnlineOrder" valuePropName="checked" labelCol={{ span: 14 }}>
                <Switch disabled={disabled} />
              </Form.Item>
            </div>
            <Row gutter={[12, 0]}>
              <Col xs={24} md={12}>
                <Form.Item label="Badge text" name="badgeText" rules={[{ max: 80 }]}>
                  <Input disabled={disabled} placeholder="e.g. Best Seller" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Search boost" name="searchBoost"
                  tooltip="Higher = ranks higher in catalog search.">
                  <NumberOrText min={0} max={99999} step={1} disabled={disabled} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Divider className="erp-studio-divider" />
            <Text strong style={{ fontSize: 13 }}>SEO meta</Text>
            <Row gutter={[12, 0]} style={{ marginTop: 8 }}>
              <Col xs={24} md={12}>
                <Form.Item label="Meta title" name="metaTitle" rules={[{ max: 180 }]}>
                  <Input disabled={disabled} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Canonical URL" name="canonicalUrl" rules={[{ max: 255 }, { type: 'url', message: 'Must be a full URL.' }]}>
                  <Input disabled={disabled} placeholder="https://…" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Meta description" name="metaDescription" rules={[{ max: 300 }]}>
              <TextArea autoSize={{ minRows: 2, maxRows: 3 }} disabled={disabled} />
            </Form.Item>
            <Form.Item label="Meta keywords" name="metaKeywords" rules={[{ max: 2000 }]}>
              <Input disabled={disabled} />
            </Form.Item>

            <Divider className="erp-studio-divider" />
            <Text strong style={{ fontSize: 13 }}>Open Graph / sharing</Text>
            <Row gutter={[12, 0]} style={{ marginTop: 8 }}>
              <Col xs={24} md={12}>
                <Form.Item label="OG title" name="ogTitle" rules={[{ max: 180 }]}>
                  <Input disabled={disabled} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="OG image URL" name="ogImageUrl"
                  rules={[{ max: 2000 }, { type: 'url', message: 'Must be a full URL.' }]}>
                  <Input disabled={disabled} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="OG description" name="ogDescription" rules={[{ max: 300 }]}>
              <TextArea autoSize={{ minRows: 2, maxRows: 3 }} disabled={disabled} />
            </Form.Item>
          </div>
        ) : null}
      </div>
    </div>
  );
}
